import interceptor from '../restServiceInterceptor';
import {
    AromaInfo,
    BankInfo,
    Configuration,
    ConfigurationColors,
    Configurations,
    DeviceGroup,
    InvoiceDetails,
    EssencePriceGroup,
    ReedInstallationGroup,
    ConsumableServiceGroup,
    Prefix,
    TimerDays,
    VatPrefix,
} from '../models/configuration-models';
import {UserGroup} from "../models/user-models";

export const useGetAllConfigurations = (): Promise<Configurations> => {
    return interceptor.get(`/configuration-service/configuration/all`, {});
};
export const useUpdateConfigurations = (
    configurationsVO: Configuration[],
    prefixes: Prefix[]
): Promise<Configuration> => {
    return interceptor.put('/configuration-service/admin/configurations', {
        configurationsVO,
        prefixes,
    });
};
export const useGetConfigColors = (): Promise<ConfigurationColors> => {
    return interceptor.get(`/configuration-service/configuration/colors`);
};
export const useGetConfigMisc = (): Promise<Configuration[]> => {
    return interceptor.get(`/configuration-service/configuration/misc`);
};
export const useGetConfigTimerDays = (): Promise<TimerDays> => {
    return interceptor.get(`/configuration-service/configuration/timerdays`);
};
export const useGetConfigCompanyInfo = (): Promise<AromaInfo> => {
    return interceptor.get(`/configuration-service/configuration/companyInfo`);
};

export const useGetConfigBankInfo = (): Promise<BankInfo> => {
    return interceptor.get(`/configuration-service/configuration/bankInfo`);
};

export const useGetConfigInvoiceDetails = (): Promise<InvoiceDetails> => {
    return interceptor.get(`/configuration-service/configuration/invoiceDetails`);
};

export const useGetVatPrefix = (group: UserGroup | undefined): Promise<VatPrefix> => {
        return group && group.mainGroup ?
        interceptor.get(`configuration-service/configuration/VAT/prefix?group=${group.mainGroup}`) :
        interceptor.get('configuration-service/configuration/VAT/prefix?group=');
};

export const useGetEssencePriceGroups = (): Promise<EssencePriceGroup[]> => {
    return interceptor.get('/configuration-service/configuration/essence-price-groups').then((priceGroups) =>
        (priceGroups as unknown as EssencePriceGroup[]).map(formatPriceGroupFromApi)
    );
};

export const useCreateEssencePriceGroup = (priceGroup: EssencePriceGroup): Promise<EssencePriceGroup> => {
    return interceptor.post('/configuration-service/admin/essence-price-groups', formatPriceGroupForApi(priceGroup))
        .then((createdPriceGroup) => formatPriceGroupFromApi(createdPriceGroup as unknown as EssencePriceGroup));
};

export const useUpdateEssencePriceGroup = (priceGroup: EssencePriceGroup): Promise<EssencePriceGroup> => {
    return interceptor.put(`/configuration-service/admin/essence-price-groups/${priceGroup.id}`, formatPriceGroupForApi(priceGroup))
        .then((updatedPriceGroup) => formatPriceGroupFromApi(updatedPriceGroup as unknown as EssencePriceGroup));
};

export const useDeleteEssencePriceGroup = (id: number): Promise<void> => {
    return interceptor.delete(`/configuration-service/admin/essence-price-groups/${id}`);
};

export const useGetDeviceGroups = (): Promise<DeviceGroup[]> => {
    return interceptor.get('/configuration-service/configuration/device-groups').then((deviceGroups) =>
        (deviceGroups as unknown as DeviceGroup[]).map(formatDeviceGroupFromApi)
    );
};

export const useCreateDeviceGroup = (deviceGroup: DeviceGroup): Promise<DeviceGroup> => {
    return interceptor.post('/configuration-service/admin/device-groups', formatDeviceGroupForApi(deviceGroup))
        .then((createdDeviceGroup) => formatDeviceGroupFromApi(createdDeviceGroup as unknown as DeviceGroup));
};

export const useUpdateDeviceGroup = (deviceGroup: DeviceGroup): Promise<DeviceGroup> => {
    return interceptor.put(`/configuration-service/admin/device-groups/${deviceGroup.id}`, formatDeviceGroupForApi(deviceGroup))
        .then((updatedDeviceGroup) => formatDeviceGroupFromApi(updatedDeviceGroup as unknown as DeviceGroup));
};

export const useDeleteDeviceGroup = (id: number): Promise<void> => {
    return interceptor.delete(`/configuration-service/admin/device-groups/${id}`);
};

export const useGetReedInstallationGroups = (): Promise<ReedInstallationGroup[]> =>
    interceptor.get('/configuration-service/configuration/reed-installation-groups')
        .then((groups) => (groups as unknown as ReedInstallationGroup[]).map(formatBonusGroupFromApi));

export const useCreateReedInstallationGroup = (group: ReedInstallationGroup): Promise<ReedInstallationGroup> =>
    interceptor.post('/configuration-service/admin/reed-installation-groups', formatBonusGroupForApi(group))
        .then((created) => formatBonusGroupFromApi(created as unknown as ReedInstallationGroup));

export const useUpdateReedInstallationGroup = (group: ReedInstallationGroup): Promise<ReedInstallationGroup> =>
    interceptor.put(`/configuration-service/admin/reed-installation-groups/${group.id}`, formatBonusGroupForApi(group))
        .then((updated) => formatBonusGroupFromApi(updated as unknown as ReedInstallationGroup));

export const useDeleteReedInstallationGroup = (id: number): Promise<void> =>
    interceptor.delete(`/configuration-service/admin/reed-installation-groups/${id}`);

export const useGetConsumableServiceGroups = (): Promise<ConsumableServiceGroup[]> =>
    interceptor.get('/configuration-service/configuration/consumable-service-groups')
        .then((groups) => (groups as unknown as ConsumableServiceGroup[]).map(formatBonusGroupFromApi));

export const useCreateConsumableServiceGroup = (group: ConsumableServiceGroup): Promise<ConsumableServiceGroup> =>
    interceptor.post('/configuration-service/admin/consumable-service-groups', formatBonusGroupForApi(group))
        .then((created) => formatBonusGroupFromApi(created as unknown as ConsumableServiceGroup));

export const useUpdateConsumableServiceGroup = (group: ConsumableServiceGroup): Promise<ConsumableServiceGroup> =>
    interceptor.put(`/configuration-service/admin/consumable-service-groups/${group.id}`, formatBonusGroupForApi(group))
        .then((updated) => formatBonusGroupFromApi(updated as unknown as ConsumableServiceGroup));

export const useDeleteConsumableServiceGroup = (id: number): Promise<void> =>
    interceptor.delete(`/configuration-service/admin/consumable-service-groups/${id}`);

const formatBonusGroupFromApi = <T extends DeviceGroup>(group: T): T => ({ ...group, bonusRate: group.bonusRate / 100 });
const formatBonusGroupForApi = <T extends DeviceGroup>(group: T): T => ({ ...group, bonusRate: Math.round(group.bonusRate * 100) });

const formatDeviceGroupFromApi = (deviceGroup: DeviceGroup): DeviceGroup => ({
    ...deviceGroup,
    bonusRate: deviceGroup.bonusRate / 100,
});

const formatDeviceGroupForApi = (deviceGroup: DeviceGroup): DeviceGroup => ({
    ...deviceGroup,
    bonusRate: Math.round(deviceGroup.bonusRate * 100),
});

const formatPriceGroupFromApi = (priceGroup: EssencePriceGroup): EssencePriceGroup => ({
    ...priceGroup,
    bonusRate: priceGroup.bonusRate / 100,
});

const formatPriceGroupForApi = (priceGroup: EssencePriceGroup): EssencePriceGroup => ({
    ...priceGroup,
    bonusRate: Math.round(priceGroup.bonusRate * 100),
});
